import type {
  BridgeValidator,
  EditorToRendererMessage,
  EditorToRendererMessageType,
  RendererToEditorMessage,
  RendererToEditorMessageType,
} from "@lureo/editor-contracts";
import type { Observable } from "rxjs";

export type EditorMessage<T extends EditorToRendererMessageType> = Extract<EditorToRendererMessage, { type: T }>;
export type RendererMessage<T extends RendererToEditorMessageType> = Extract<RendererToEditorMessage, { type: T }>;

/** The renderer's one line to its editor: the contract's messages each way, whatever carries them. */
export interface RendererBridgePort {
  /** Starts listening to the editor; once, as RendererBridge_Init does. */
  init(): void;
  /** Posts a message to the editor; dropped while no editor can receive it. */
  send(message: RendererToEditorMessage): void;
  /** Messages of one type from the editor. */
  on<T extends EditorToRendererMessageType>(type: T): Observable<EditorMessage<T>>;
  /** Every message from the editor the renderer acts on. */
  readonly received$: Observable<EditorToRendererMessage>;
  /** The messages added since the bridge's first version that this renderer handles (rendererLoaded.accepts). */
  readonly accepts: readonly EditorToRendererMessageType[];
  dispose(): void;
}

/** Structurally a HexLogger: the RendererBridge module's serialized logger. */
export interface BridgeLogger {
  log(message: string, detail?: string): void;
  warn(message: string, detail?: string): void;
}

export const silentBridgeLogger: BridgeLogger = { log: () => undefined, warn: () => undefined };

/** The contract check a dev or test build runs on every message; it logs what it finds and changes nothing. */
export type BridgeContractCheck = Pick<BridgeValidator, "editorMessageProblems" | "rendererMessageProblems">;

export interface RendererBridgeOptions {
  readonly logger?: BridgeLogger;
  readonly contractCheck?: BridgeContractCheck;
}
